export interface CustomerAddress {
    street: string
    house_number: string
    postal_code: string
    city: string
    country: string
}

export interface CustomerInvoiceStats {
    invoice_count: number
}

export interface Customer {
    id: string
    company_name: string
    address_extra_line: string | null
    address: CustomerAddress
    email: string | null
    fixed_cc_email: string | null
    phone_number: string | null
    vat_number: string | null
    default_payment_term_days: number | null
    archived_at: string | null
    created_at: string
    updated_at: string
    stats: CustomerInvoiceStats | null
}

export type CustomerDraft = Omit<Customer, 'id' | 'stats' | 'archived_at' | 'created_at' | 'updated_at'>

export const EMPTY_CUSTOMER_ADDRESS: CustomerAddress = {
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    country: 'NL',
}

export const EMPTY_CUSTOMER_DRAFT: CustomerDraft = {
    company_name: '',
    address_extra_line: null,
    address: EMPTY_CUSTOMER_ADDRESS,
    email: null,
    fixed_cc_email: null,
    phone_number: null,
    vat_number: null,
    default_payment_term_days: null,
}

export function customerInitials(customer: Pick<Customer, 'company_name'>): string {
    const words = customer.company_name.trim().split(/\s+/).filter(Boolean)
    if (words.length === 0) return '?'
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
    return (words[0][0] + words[1][0]).toUpperCase()
}

const AVATAR_COLORS = [
    'bg-sky-500',
    'bg-emerald-500',
    'bg-amber-500',
    'bg-violet-500',
    'bg-rose-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-orange-500',
]

export function customerAvatarColor(customer: Pick<Customer, 'company_name'>): string {
    let hash = 0
    for (const char of customer.company_name) {
        hash = (hash * 31 + char.charCodeAt(0)) % 997
    }
    return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

export function formatCustomerAddress(address: CustomerAddress): string {
    const streetPart = [address.street, address.house_number].filter(Boolean).join(' ')
    const cityPart = [address.postal_code, address.city].filter(Boolean).join(' ')
    return [streetPart, cityPart].filter(Boolean).join(', ')
}

export function customerMatchesSearch(customer: Customer, query: string): boolean {
    const q = query.trim().toLowerCase()
    if (!q) return true
    const haystack = [
        customer.company_name,
        customer.address_extra_line,
        customer.email,
        customer.fixed_cc_email,
        customer.phone_number,
        customer.vat_number,
        customer.address.city,
        customer.address.street,
        customer.address.postal_code,
    ].filter(Boolean).join(' ').toLowerCase()
    return q.split(/\s+/).every(term => haystack.includes(term))
}
